import { FC, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@ps/ds/components/blocks/Item/Item'
import { Separator } from '@ps/ds/components/primitives/Separator/Separator'
import { Spinner } from '@ps/ds/components/primitives/Spinner/Spinner'
import { formatString } from '@ps/tools/helpers/string'
import { ChevronRight, Mail, MessageSquareMore, Phone } from 'lucide-react'
import { NS } from 'src/constants/i18n'

import { CONTACT_TYPES, PEST_SHARE_CONTACT } from '../constants/contact'
import { ContactTypes } from '../types/contact'

type CommonItemContentProps = {
  /**
   * Defines if the title should append "Pest Share" as the provider
   */
  hasLongTitle?: boolean
  /**
   * Defines which contact type is being used to determine the label and description
   */
  type: ContactTypes
}

const config: Record<
  ContactTypes,
  { icon: ReactNode; description?: string; href?: string; onClick?: () => void }
> = {
  [CONTACT_TYPES.PHONE]: {
    href: `tel:${PEST_SHARE_CONTACT.phone}`,
    icon: <Phone className="text-navy-400" />,
    description: formatString.toFormattedPhoneNumber(PEST_SHARE_CONTACT.phone),
  },
  [CONTACT_TYPES.EMAIL]: {
    href: `mailto:${PEST_SHARE_CONTACT.email}`,
    icon: <Mail className="text-navy-400" />,
    description: PEST_SHARE_CONTACT.email,
  },
  [CONTACT_TYPES.CHAT]: {
    icon: <MessageSquareMore className="text-navy-400" />,
    onClick: () => {
      window?.HubSpotConversations?.widget.open()
    },
  },
}

const CommonItemContent: FC<CommonItemContentProps & { isChatLoading: boolean }> = ({
  hasLongTitle,
  isChatLoading,
  type,
}) => {
  const { t } = useTranslation(NS.TRACKING)
  const { description } = config[type]
  const icon =
    type === CONTACT_TYPES.CHAT && isChatLoading ? (
      <Spinner className="size-5 text-navy-400" />
    ) : (
      config[type].icon
    )

  return (
    <>
      <ItemMedia variant="icon">
        <div className="w-10 h-10 p-[10px] bg-navy-100 rounded-full flex items-center justify-center">
          {icon}
        </div>
      </ItemMedia>

      <ItemContent className="min-w-0">
        {hasLongTitle ? (
          <ItemTitle>
            {t(`contact.options.${type}.labelWithProvider`, {
              providerName: PEST_SHARE_CONTACT.providerName,
            })}
          </ItemTitle>
        ) : (
          <ItemTitle>{t(`contact.options.${type}.label`)}</ItemTitle>
        )}

        {description && (
          <ItemDescription className="hidden truncate md:block">{description}</ItemDescription>
        )}
      </ItemContent>

      <ItemActions>
        <ChevronRight />
      </ItemActions>
    </>
  )
}

type GenericContactItemProps = CommonItemContentProps & {
  asChildOf?: 'button' | 'anchor'
  hasSeparator?: boolean
}

export const GenericContactItem: FC<GenericContactItemProps> = ({
  asChildOf,
  hasSeparator,
  ...contentProps
}) => {
  const [isChatLoading, setIsChatLoading] = useState(
    () => !window.HubSpotConversations?.widget.status().loaded,
  )

  useEffect(() => {
    if (contentProps.type !== CONTACT_TYPES.CHAT) return

    let isActive = true

    const handleWidgetLoaded = () => {
      if (!isActive) return
      setIsChatLoading(false)
    }

    const handleHubSpotReady = () => {
      if (!isActive) return
      const hubSpot = window.HubSpotConversations

      if (!hubSpot) return

      hubSpot.on('widgetLoaded', handleWidgetLoaded)

      // subscribe before checking so a load between the two is not missed
      if (hubSpot.widget.status().loaded) {
        setIsChatLoading(false)
      }
    }

    // 1. HubSpot already loaded: it has run its queue, so listen for the widget directly
    if (window.HubSpotConversations) {
      window.HubSpotConversations.on('widgetLoaded', handleWidgetLoaded)

      // the widget may have loaded between the initial render and subscribing
      if (window.HubSpotConversations.widget.status().loaded) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setIsChatLoading(false)
      }

      return () => {
        isActive = false
        window.HubSpotConversations?.off?.('widgetLoaded', handleWidgetLoaded)
      }
    }

    // 2. HubSpot not loaded yet: queue a callback for it to run once it is ready
    window.hsConversationsOnReady = window.hsConversationsOnReady || []
    window.hsConversationsOnReady.push(handleHubSpotReady)

    return () => {
      isActive = false
      window.HubSpotConversations?.off?.('widgetLoaded', handleWidgetLoaded)
      // remove the callback from the global array to avoid memory leaks
      window.hsConversationsOnReady = window.hsConversationsOnReady?.filter(
        (callback) => callback !== handleHubSpotReady,
      )
    }
  }, [contentProps.type])

  return (
    <>
      <Item asChild className="w-full py-0 md:flex-nowrap">
        {asChildOf === 'anchor' ? (
          <a href={config[contentProps.type].href} className="my-4 w-full">
            <CommonItemContent {...contentProps} isChatLoading={isChatLoading} />

            {hasSeparator && (
              <div className="w-full md:hidden">
                <Separator />
              </div>
            )}
          </a>
        ) : (
          <button type="button" className="my-4 w-full" onClick={config[contentProps.type].onClick}>
            <CommonItemContent {...contentProps} isChatLoading={isChatLoading} />

            {hasSeparator && (
              <div className="w-full md:hidden">
                <Separator />
              </div>
            )}
          </button>
        )}
      </Item>

      {hasSeparator && (
        <div className="w-full hidden md:block">
          <Separator />
        </div>
      )}
    </>
  )
}
